/**
 * The calendar (DLY-005): every day from the first daily table to today, marked with
 * how the player did on it, and a way into any past day's table as the archive.
 *
 * Replaces the results screen's "Calendar: coming soon" (D-026), and opens from the top
 * bar too. A popup, not a page: the address stays the bare site (URL-001).
 *
 * Days after the server's today are drawn but cannot be opened. That is only UX: the
 * server refuses them regardless (N7). Archive plays never count (D-008).
 */
import { CALENDAR_COPY, TIMEZONE } from '../config/site.ts';
import type { DayRecord } from '../daily/history.ts';
import {
  addMonths,
  describeDay,
  monthGrid,
  monthIndex,
  monthOf,
  type CalendarDay,
  type YearMonth,
} from '../calendar/month.ts';

export interface CalendarOptions {
  /** The server's today; undefined before the daily game has started. */
  readonly todayKey: string | undefined;
  readonly firstDateKey: string | undefined;
  /** Before launch: when the first table arrives, to say so. */
  readonly firstRolloverMs: number | undefined;
  /** The day on the table now, to mark it. */
  readonly playingKey: string | undefined;
  record(dateKey: string): DayRecord | undefined;
  /** A playable day was chosen. */
  onPick(dateKey: string): void;
}

export interface CalendarDialog {
  open(): void;
}

const MONTH_TITLE = new Intl.DateTimeFormat('en-AU', {
  timeZone: 'UTC',
  month: 'long',
  year: 'numeric',
});
const DAY_NAME = new Intl.DateTimeFormat('en-AU', {
  timeZone: 'UTC',
  day: 'numeric',
  month: 'long',
});

function dayName(dateKey: string): string {
  return DAY_NAME.format(Date.parse(`${dateKey}T00:00:00Z`));
}

function describe(day: CalendarDay, playing: boolean): string {
  const c = CALENDAR_COPY;
  const parts = [dayName(day.dateKey)];
  if (day.isToday) parts.push(c.today);
  if (day.label !== undefined) parts.push(day.label);
  else if (day.state === 'unfinished') parts.push(c.legend.unfinished);
  else if (day.state === 'future') parts.push(c.notOut);
  else if (day.state === 'unplayed' && !day.isToday) parts.push(c.notPlayed);
  if (playing) parts.push(c.playing);
  return parts.join(': ');
}

export function createCalendarDialog(host: HTMLElement, options: CalendarOptions): CalendarDialog {
  const c = CALENDAR_COPY;
  const dialog = document.createElement('dialog');
  dialog.className = 'settings calendar';
  dialog.setAttribute('aria-labelledby', 'calendar-title');
  dialog.innerHTML = `
    <form method="dialog" class="settings__card calendar__card">
      <header class="settings__header">
        <h2 class="settings__title" id="calendar-title">${c.title}</h2>
        <button type="submit" class="btn btn--icon" aria-label="${c.close}">&times;</button>
      </header>
      <div class="calendar__body" data-body></div>
    </form>
  `;
  host.append(dialog);
  const body = dialog.querySelector<HTMLDivElement>('[data-body]');
  if (body === null) throw new Error('calendar: the body is missing');

  dialog.addEventListener('click', (event) => {
    if (event.target === dialog) dialog.close();
  });

  const { todayKey, firstDateKey } = options;
  const first = firstDateKey === undefined ? undefined : monthOf(firstDateKey);
  const last = todayKey === undefined ? undefined : monthOf(todayKey);
  let shown: YearMonth | undefined =
    options.playingKey !== undefined ? monthOf(options.playingKey) : last;

  function renderNotYet(): void {
    const note = document.createElement('p');
    note.className = 'calendar__note';
    const date =
      options.firstRolloverMs === undefined
        ? undefined
        : new Intl.DateTimeFormat('en-AU', {
            timeZone: TIMEZONE,
            day: 'numeric',
            month: 'long',
          }).format(options.firstRolloverMs);
    note.textContent = date === undefined ? c.archiveNote : c.notYet(date);
    body?.replaceChildren(note);
  }

  function render(): void {
    if (
      todayKey === undefined ||
      firstDateKey === undefined ||
      shown === undefined ||
      first === undefined ||
      last === undefined
    ) {
      renderNotYet();
      return;
    }
    const month = shown;

    const nav = document.createElement('div');
    nav.className = 'calendar__nav';
    const prev = document.createElement('button');
    prev.type = 'button';
    prev.className = 'btn btn--quiet btn--icon';
    prev.textContent = '‹';
    prev.setAttribute('aria-label', c.previous);
    prev.disabled = monthIndex(month) <= monthIndex(first);
    const title = document.createElement('h3');
    title.className = 'calendar__month';
    title.textContent = MONTH_TITLE.format(Date.UTC(month.year, month.month - 1, 1));
    const next = document.createElement('button');
    next.type = 'button';
    next.className = 'btn btn--quiet btn--icon';
    next.textContent = '›';
    next.setAttribute('aria-label', c.next);
    next.disabled = monthIndex(month) >= monthIndex(last);
    prev.addEventListener('click', () => {
      shown = addMonths(month, -1);
      render();
    });
    next.addEventListener('click', () => {
      shown = addMonths(month, 1);
      render();
    });
    nav.append(prev, title, next);

    const grid = document.createElement('div');
    grid.className = 'calendar__grid';
    for (const name of c.weekdays) {
      const head = document.createElement('span');
      head.className = 'calendar__weekday';
      head.textContent = name;
      grid.append(head);
    }
    for (const key of monthGrid(month).flat()) {
      if (key === undefined) {
        grid.append(document.createElement('span'));
        continue;
      }
      const day = describeDay(key, todayKey, firstDateKey, options.record(key));
      const playing = key === options.playingKey;
      const cell = document.createElement(day.playable ? 'button' : 'span');
      cell.className = `calendar__day calendar__day--${day.state}`;
      cell.classList.toggle('calendar__day--today', day.isToday);
      cell.classList.toggle('calendar__day--playing', playing);
      cell.textContent = String(day.day);
      cell.setAttribute('aria-label', describe(day, playing));
      cell.title = describe(day, playing);
      if (cell instanceof HTMLButtonElement) {
        cell.type = 'button';
        cell.addEventListener('click', () => {
          dialog.close();
          options.onPick(key);
        });
      }
      grid.append(cell);
    }

    const legend = document.createElement('ul');
    legend.className = 'calendar__legend';
    for (const [state, text] of Object.entries(c.legend)) {
      const item = document.createElement('li');
      const dot = document.createElement('span');
      dot.className = `calendar__dot calendar__dot--${state}`;
      dot.setAttribute('aria-hidden', 'true');
      item.append(dot, text);
      legend.append(item);
    }

    const note = document.createElement('p');
    note.className = 'calendar__note';
    note.textContent = c.archiveNote;

    body?.replaceChildren(nav, grid, legend, note);
  }

  return {
    open() {
      shown = options.playingKey !== undefined ? monthOf(options.playingKey) : last;
      render();
      dialog.showModal();
    },
  };
}
